import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import { spacing } from "@mui/system";
import PreFinanceDossierDetailDialog from "../../components/PreFinanceDossierDetailDialog";
import ViewClaimDialog from "../../components/ViewClaimDialog";
import ViewExciseClaimDialog from "../../components/ViewExciseClaimDialog";
import VatPreFinancePrepareDialog from "../../components/VatPreFinancePrepareDialog";
import ExcisePreFinancePrepareDialog from "../../components/ExcisePreFinancePrepareDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import DataGridExport from "../../components/DataGridExport";
import { getPreFinanceYearOptions } from "../../utils/preFinanceYearOptions";
import { emptyDataGridFilterModel, toServerGridFilterParams, areDataGridFilterModelsEqual } from "../../utils/dataGridServerFilter";
import { fetchAllPagedItems, OVERVIEW_EXPORT_PAGE_SIZE } from "../../utils/fetchAllPagedItems";
import { VAT_MAX_COUNTRY_ID, EXCISE_MIN_COUNTRY_ID } from "../../utils/countryProductUtils";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const initialFilters = {
  preFinanceType: "",
  countryId: "",
  year: "",
  monthFrom: "",
  monthTo: "",
  finalized: "",
};

const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: moment().month(index).format("MMM"),
}));

const yearOptions = getPreFinanceYearOptions();

const formatMoney = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }

  return value.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPeriod = (row) => {
  if (!row.year || !row.startMonth || !row.endMonth) {
    return "—";
  }

  const period =
    row.startMonth === row.endMonth
      ? String(row.startMonth).padStart(2, "0")
      : `${String(row.startMonth).padStart(2, "0")}–${String(row.endMonth).padStart(2, "0")}`;
  return `${row.year} ${period} ${row.periodType === 2 ? "p/Q" : "p/M"}`;
};

const PrefinanceOverviewToolbar = ({ columns, fetchRows, fileName, onError }) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <DataGridExport
      variant="toolbar"
      columns={columns}
      fetchRows={fetchRows}
      fileName={fileName}
      onError={onError}
    />
    <Box sx={{ flexGrow: 1 }} />
    <GridToolbarQuickFilter debounceMs={250} />
  </GridToolbarContainer>
);

const SummaryValue = ({ label, value }) => (
  <Box sx={{ textAlign: "left", minWidth: 88 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
  </Box>
);

const CustomerFinancial = ({ customerId, customerNumber, scope = "customer" }) => {
  const isGlobalScope = scope === "global";
  const { hasPermission } = usePermissions();
  const canCreatePreFinance = hasPermission(CAPABILITIES.prefinanceCreate, "X");
  const canFinalizePreFinance = hasPermission(CAPABILITIES.prefinanceFinalize, "X");
  const canUnfinalizePreFinance = hasPermission(CAPABILITIES.prefinanceUnfinalize, "X");
  const [filters, setFilters] = useState(initialFilters);
  const [overview, setOverview] = useState({
    items: [],
    totalCount: 0,
    totals: {
      dossierCount: 0,
      finalizedCount: 0,
      legacyCount: 0,
      totalRefundAmount: 0,
      totalPayoutAmount: 0,
      totalLinkedInvoiceCount: 0,
      totalLinkedExciseClaimCount: 0,
    },
  });
  const [countries, setCountries] = useState([]);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [isVatPrepareOpen, setIsVatPrepareOpen] = useState(false);
  const [isExcisePrepareOpen, setIsExcisePrepareOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    dossier: null,
  });
  const [deletingDossierId, setDeletingDossierId] = useState(null);
  const [viewClaimDialog, setViewClaimDialog] = useState({
    isOpen: false,
    title: "",
    params: null,
    custInfo: [],
    subTitle: "",
  });
  const [viewExciseClaimDialog, setViewExciseClaimDialog] = useState({
    isOpen: false,
    title: "",
    params: null,
    countryData: [],
    downloadFile: true,
    custInfo: [],
  });
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });
  const [filterModel, setFilterModel] = useState(emptyDataGridFilterModel);
  const [sortModel, setSortModel] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const buildOverviewParams = useCallback(
    (pageNumber, pageSizeValue) => {
      const params = {
        page: pageNumber,
        pageSize: pageSizeValue,
        ...toServerGridFilterParams(filterModel),
      };

      if (sortModel[0]?.field) {
        params.sortField = sortModel[0].field;
        params.sortDirection = sortModel[0].sort || "asc";
      }

      if (!isGlobalScope && customerId) {
        params.entityId = customerId;
      }

      if (filters.preFinanceType !== "") {
        params.preFinanceType = Number(filters.preFinanceType);
      }
      if (filters.countryId !== "") {
        params.countryId = Number(filters.countryId);
      }
      if (filters.year !== "") {
        params.year = Number(filters.year);
      }
      if (filters.monthFrom !== "") {
        params.monthFrom = Number(filters.monthFrom);
      }
      if (filters.monthTo !== "") {
        params.monthTo = Number(filters.monthTo);
      }
      if (filters.finalized !== "") {
        params.finalized = filters.finalized === "true";
      }

      return params;
    },
    [customerId, filterModel, filters, isGlobalScope, sortModel]
  );

  const fetchOverview = useCallback(
    async (signal) => {
      setIsLoading(true);
      setError("");

      try {
        const response = await axios.get("/api/v1/preFinance/overview", {
          params: buildOverviewParams(pagination.page + 1, pagination.pageSize),
          signal,
        });
        setOverview(response.data);
      } catch (requestError) {
        if (requestError.code !== "ERR_CANCELED") {
          console.error("Unable to load prefinance overview:", requestError);
          setError(requestError.response?.data?.message || "The prefinance overview could not be loaded.");
        }
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [buildOverviewParams, pagination]
  );

  const handleFetchExportRows = useCallback(async () => {
    const result = await fetchAllPagedItems({
      pageSize: OVERVIEW_EXPORT_PAGE_SIZE,
      requestPage: async (pageNumber, exportPageSize) => {
        const response = await axios.get("/api/v1/preFinance/overview", {
          params: buildOverviewParams(pageNumber, exportPageSize),
        });
        return {
          items: response.data.items || [],
          totalCount: response.data.totalCount || 0,
        };
      },
    });

    if (result.truncated) {
      setError(`Export limited to ${result.items.length} of ${result.totalCount} matching dossiers.`);
    }

    return result.items;
  }, [buildOverviewParams]);

  useEffect(() => {
    const controller = new AbortController();
    fetchOverview(controller.signal);
    return () => controller.abort();
  }, [fetchOverview]);

  useEffect(() => {
    const controller = new AbortController();
    const handleFetchCountries = async () => {
      try {
        const response = await axios.get("/api/v1/allCountries", {
          signal: controller.signal,
        });
        setCountries(
          (response.data || []).sort((left, right) =>
            (left.countryCode || "").localeCompare(right.countryCode || "")
          )
        );
      } catch (requestError) {
        if (requestError.code !== "ERR_CANCELED") {
          console.error("Unable to load countries:", requestError);
        }
      }
    };

    handleFetchCountries();
    return () => controller.abort();
  }, []);

  const countryOptions = useMemo(() => {
    if (filters.preFinanceType === "0") {
      return countries.filter((country) => country.countryId <= VAT_MAX_COUNTRY_ID);
    }

    if (filters.preFinanceType === "1") {
      return countries.filter((country) => country.countryId >= EXCISE_MIN_COUNTRY_ID);
    }

    return countries;
  }, [countries, filters.preFinanceType]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setPagination((currentPagination) => ({
      ...currentPagination,
      page: 0,
    }));
    setFilters((currentFilters) => {
      if (name === "year" && value === "") {
        return {
          ...currentFilters,
          year: "",
          monthFrom: "",
          monthTo: "",
        };
      }

      if (name === "preFinanceType") {
        const nextCountryOptions =
          value === "0"
            ? countries.filter((country) => country.countryId <= VAT_MAX_COUNTRY_ID)
            : value === "1"
            ? countries.filter((country) => country.countryId >= EXCISE_MIN_COUNTRY_ID)
            : countries;
        const countryStillValid =
          currentFilters.countryId === "" ||
          nextCountryOptions.some((country) => String(country.countryId) === String(currentFilters.countryId));

        return {
          ...currentFilters,
          preFinanceType: value,
          countryId: countryStillValid ? currentFilters.countryId : "",
        };
      }

      return {
        ...currentFilters,
        [name]: value,
      };
    });
  };

  const handleResetFilters = () => {
    setFilters(initialFilters);
    setPagination((currentPagination) => ({
      ...currentPagination,
      page: 0,
    }));
  };

  const handlePageChange = (page) => {
    setPagination((currentPagination) => ({
      ...currentPagination,
      page,
    }));
  };

  const handlePageSizeChange = (pageSize) => {
    setPagination({
      page: 0,
      pageSize,
    });
  };

  const handleFilterModelChange = (model) => {
    const nextModel = model || emptyDataGridFilterModel;
    if (areDataGridFilterModelsEqual(filterModel, nextModel)) {
      return;
    }

    setPagination((currentPagination) => ({
      ...currentPagination,
      page: 0,
    }));
    setFilterModel(nextModel);
  };

  const handleSortModelChange = (model) => {
    setPagination((currentPagination) => ({
      ...currentPagination,
      page: 0,
    }));
    setSortModel(model || []);
  };

  const handleOpenDetails = useCallback((dossier) => {
    setSelectedDossier(dossier);
  }, []);

  const handleCloseDetails = () => {
    setSelectedDossier(null);
  };

  const handleRequestDelete = useCallback((dossier) => {
    if (dossier.isFinalized) {
      setError("Finalized pre-finance dossiers cannot be deleted.");
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: "Delete prefinance dossier",
      subTitle: `Delete ${
        dossier.preFinanceCode || `dossier #${dossier.preFinanceId}`
      } permanently? Its linked source records will be unlinked.`,
      dossier,
    });
  }, []);

  const handleDeleteDossier = async () => {
    const dossier = confirmDialog.dossier;
    if (!dossier) {
      return;
    }

    if (dossier.isFinalized) {
      setError("Finalized pre-finance dossiers cannot be deleted.");
      return;
    }

    setDeletingDossierId(dossier.preFinanceId);
    setError("");
    setConfirmDialog((currentDialog) => ({
      ...currentDialog,
      isOpen: false,
    }));

    try {
      await axios.delete(`/api/v1/preFinance/${dossier.preFinanceId}`);
      setSuccessMessage(
        `${dossier.preFinanceCode || `Dossier #${dossier.preFinanceId}`} deleted successfully.`
      );
      if (selectedDossier?.preFinanceId === dossier.preFinanceId) {
        setSelectedDossier(null);
      }

      if (overview.items.length === 1 && pagination.page > 0) {
        setPagination((currentPagination) => ({
          ...currentPagination,
          page: currentPagination.page - 1,
        }));
      } else {
        fetchOverview(new AbortController().signal);
      }
    } catch (requestError) {
      console.error("Unable to delete prefinance dossier:", requestError);
      setError(
        requestError.response?.data?.message ||
          "The prefinance dossier could not be deleted."
      );
    } finally {
      setDeletingDossierId(null);
    }
  };

  const handleVatCreated = (result) => {
    const codes = result.createdDossiers.map((dossier) => dossier.preFinanceCode).join(", ");
    setSuccessMessage(
      `${result.createdDossiers.length} VAT dossier(s) created successfully: ${codes}`
    );
    fetchOverview(new AbortController().signal);
  };

  const handleExciseCreated = (result) => {
    const codes = result.createdDossiers.map((dossier) => dossier.preFinanceCode).join(", ");
    setSuccessMessage(
      `${result.createdDossiers.length} Excise dossier(s) created successfully: ${codes}`
    );
    fetchOverview(new AbortController().signal);
  };


  const handleDossierFinalized = (result) => {
    const updatedDossier = result.dossiers?.find(
      (item) => item.preFinanceId === selectedDossier?.preFinanceId
    );

    if (updatedDossier && selectedDossier) {
      setSelectedDossier({
        ...selectedDossier,
        isFinalized: true,
        payoutDate: updatedDossier.payoutDate,
      });
    }

    const newlyFinalized = result.finalizedCount || 0;
    const alreadyFinalized = result.alreadyFinalizedCount || 0;
    setSuccessMessage(
      newlyFinalized > 0
        ? `${newlyFinalized} dossier(s) finalized successfully${
            alreadyFinalized > 0 ? ` (${alreadyFinalized} already finalized).` : "."
          }`
        : `${alreadyFinalized} dossier(s) were already finalized for this payout date.`
    );
    fetchOverview(new AbortController().signal);
  };

  const handleDossierUnfinalized = (result) => {
    if (selectedDossier) {
      setSelectedDossier({
        ...selectedDossier,
        isFinalized: false,
        payoutDate: null,
      });
    }

    const reopenedCount = result.unfinalizedCount || 0;
    const alreadyOpenCount = result.alreadyOpenCount || 0;
    setSuccessMessage(
      reopenedCount > 0
        ? `${reopenedCount} dossier(s) reopened successfully${
            alreadyOpenCount > 0 ? ` (${alreadyOpenCount} already open).` : "."
          }`
        : `${alreadyOpenCount} dossier(s) were already open.`
    );
    fetchOverview(new AbortController().signal);
  };

  const handlePayoutAmountAdjusted = (result) => {
    if (selectedDossier && result?.preFinanceId === selectedDossier.preFinanceId) {
      setSelectedDossier({
        ...selectedDossier,
        payoutAmount: result.payoutAmount,
        calculatedPayoutAmount: result.calculatedPayoutAmount,
        payoutAdjustmentReason: result.payoutAdjustmentReason,
      });
    }

    setSuccessMessage("Payout amount updated successfully.");
    fetchOverview(new AbortController().signal);
  };

  const handleOpenLinkedClaim = useCallback(
    async (row) => {
      if (!row?.claimId) {
        return;
      }

      const claimCustomerId = row.entityId || customerId;
      const claimCustomerNumber = row.customerNumber || customerNumber;

      if (row.preFinanceType === 0) {
        setViewClaimDialog({
          isOpen: true,
          title: "View Claim",
          params: { claimId: row.claimId },
          custInfo: [{ customerId: claimCustomerId, customerNumber: claimCustomerNumber }],
          subTitle: `View details for ${row.claimCode || row.claimId}`,
        });
        return;
      }

      try {
        const response = await axios.get(`/api/v1/exciseClaims/${row.claimId}`);
        const claim = response.data;
        setViewExciseClaimDialog({
          isOpen: true,
          title: "View Excise Claim",
          params: claim,
          countryData: [],
          downloadFile: true,
          custInfo: [{ customerId: claimCustomerId, customerNumber: claimCustomerNumber }],
          subTitle: `View details for ${claim.claimCode || row.claimCode || row.claimId}`,
        });
      } catch (requestError) {
        console.error("Unable to load linked Excise claim:", requestError);
        setError(
          requestError.response?.data?.message ||
            `Unable to load Excise claim ${row.claimCode || row.claimId}.`
        );
      }
    },
    [customerId, customerNumber]
  );

  const columns = useMemo(
    () => [
      ...(isGlobalScope
        ? [
            {
              field: "customerNumber",
              headerName: "Customer number",
              width: 125,
              valueGetter: ({ row }) => row.customerNumber || "—",
            },
            {
              field: "customerName",
              headerName: "Customer name",
              minWidth: 200,
              flex: 1.2,
              valueGetter: ({ row }) => row.customerName || "—",
            },
          ]
        : []),
      {
        field: "preFinanceCode",
        headerName: "Pre-finance code",
        minWidth: 175,
        flex: 0.75,
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <Typography variant="body2" noWrap sx={{ fontFamily: "monospace" }}>
              {row.preFinanceCode || `#${row.preFinanceId}`}
            </Typography>
            {row.isLegacy && (
              <Tooltip title={row.legacyMismatchReason || "Historical legacy dossier"}>
                <Chip label="Legacy" color="warning" size="small" />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        field: "version",
        headerName: "Version",
        width: 80,
        valueFormatter: ({ value }) => value ?? "—",
      },
      { field: "productLabel", headerName: "Product", width: 90 },
      {
        field: "countryCode",
        headerName: "Country",
        width: 100,
        valueGetter: ({ row }) => row.countryCode || row.countryName || "—",
      },
      {
        field: "period",
        headerName: "Period",
        width: 145,
        valueGetter: ({ row }) => formatPeriod(row),
      },
      {
        field: "claimCode",
        headerName: "Claim",
        minWidth: 130,
        flex: 0.7,
        renderCell: ({ row }) =>
          row.claimId ? (
            <Button
              type="button"
              variant="text"
              color="primary"
              size="small"
              onClick={() => handleOpenLinkedClaim(row)}
              aria-label={`View linked claim ${row.claimCode || row.claimId}`}
              sx={{
                minWidth: "auto",
                px: 0,
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                fontFamily: "monospace",
                fontSize: "inherit",
              }}
            >
              {row.claimCode || `#${row.claimId}`}
            </Button>
          ) : (
            "—"
          ),
      },
      {
        field: "claimState",
        headerName: "Claim state",
        minWidth: 120,
        flex: 0.55,
        valueGetter: ({ row }) => row.claimStateDescription || row.claimStateShortCode || "—",
      },
      {
        field: "refundAmount",
        headerName: "Refund",
        width: 125,
        align: "right",
        headerAlign: "right",
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "payoutAmount",
        headerName: "Payout",
        width: 125,
        align: "right",
        headerAlign: "right",
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "isFinalized",
        headerName: "Finalized",
        width: 105,
        renderCell: ({ value }) => (
          <Chip label={value ? "Yes" : "No"} color={value ? "success" : "default"} size="small" />
        ),
      },
      {
        field: "payoutDate",
        headerName: "Payout date",
        width: 120,
        valueFormatter: ({ value }) => (value ? moment(value).format("DD-MM-YYYY") : "—"),
      },
      {
        field: "sourceCount",
        headerName: "Sources",
        width: 90,
        align: "right",
        headerAlign: "right",
        valueGetter: ({ row }) =>
          row.preFinanceType === 0 ? row.linkedInvoiceCount : row.linkedExciseClaimCount,
      },
      {
        field: "actions",
        headerName: "",
        width: 100,
        sortable: false,
        filterable: false,
        disableExport: true,
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center">
            <Tooltip title="View linked source records">
              <IconButton
                aria-label={`View dossier ${row.preFinanceCode || row.preFinanceId}`}
                onClick={() => handleOpenDetails(row)}
                size="small"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                row.isFinalized
                  ? "Finalized pre-finance dossiers cannot be deleted"
                  : "Delete prefinance dossier"
              }
            >
              <span>
                <IconButton
                  aria-label={`Delete dossier ${row.preFinanceCode || row.preFinanceId}`}
                  onClick={() => handleRequestDelete(row)}
                  color="error"
                  size="small"
                  disabled={row.isFinalized || deletingDossierId === row.preFinanceId}
                >
                  {deletingDossierId === row.preFinanceId ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [
      deletingDossierId,
      handleOpenDetails,
      handleOpenLinkedClaim,
      handleRequestDelete,
      isGlobalScope,
    ]
  );

  const handleExportError = useCallback((requestError) => {
    setError(
      requestError.response?.data?.message || "The prefinance overview could not be exported."
    );
  }, []);

  const selectedCustomerId = selectedDossier?.entityId || customerId;
  const selectedCustomerNumber = selectedDossier?.customerNumber || customerNumber;

  const listCardHeight = isGlobalScope
    ? { xs: "calc(100vh - 200px)", md: "calc(100vh - 220px)" }
    : { xs: "calc(100vh - 420px)", md: "calc(100vh - 440px)" };

  return (
    <React.Fragment>
      <Helmet title={isGlobalScope ? "All Pre-Financing" : "Customer Financials"} />
      <Divider my={3} />
      <Card
        mb={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: listCardHeight,
          minHeight: 360,
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            "&:last-child": { pb: 2 },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
            <Box>
              <Typography variant="h6">
                {isGlobalScope ? "All pre-financing" : "Prefinance dossiers"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isGlobalScope
                  ? "Organization-wide prefinance dossier overview"
                  : "Overview and dossier management"}
              </Typography>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              {!isGlobalScope && (
                <>
                  <Tooltip
                    title={
                      canCreatePreFinance
                        ? "Prepare VAT prefinance"
                        : "The prefinance.create permission is required"
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsVatPrepareOpen(true)}
                        disabled={!canCreatePreFinance}
                        aria-label="Create pre-finance VAT"
                      >
                        Create pre-finance VAT
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      canCreatePreFinance
                        ? "Prepare Excise prefinance"
                        : "The prefinance.create permission is required"
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsExcisePrepareOpen(true)}
                        disabled={!canCreatePreFinance}
                        aria-label="Create pre-finance Excise"
                      >
                        Create pre-finance Excise
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleResetFilters}
                aria-label="Reset prefinance filters"
              >
                Reset filters
              </Button>
            </Box>
          </Box>

          {successMessage && (
            <Alert severity="success" onClose={() => setSuccessMessage("")} sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Product"
                name="preFinanceType"
                value={filters.preFinanceType}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All products</MenuItem>
                <MenuItem value="0">VAT</MenuItem>
                <MenuItem value="1">Excise</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Country"
                name="countryId"
                value={filters.countryId}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All countries</MenuItem>
                {countryOptions.map((country) => (
                  <MenuItem key={country.countryId} value={country.countryId}>
                    {country.countryCode} - {country.countryName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Year"
                name="year"
                value={filters.year}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All years</MenuItem>
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                select
                fullWidth
                size="small"
                label="From"
                name="monthFrom"
                value={filters.monthFrom}
                onChange={handleFilterChange}
                disabled={!filters.year}
              >
                <MenuItem value="">Start of year</MenuItem>
                {monthOptions.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                select
                fullWidth
                size="small"
                label="To"
                name="monthTo"
                value={filters.monthTo}
                onChange={handleFilterChange}
                disabled={!filters.year}
              >
                <MenuItem value="">End of year</MenuItem>
                {monthOptions.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Finalized"
                name="finalized"
                value={filters.finalized}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Finalized</MenuItem>
                <MenuItem value="false">Open</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="flex-start"
            gap={4}
            p={2}
            mb={2}
            border={1}
            borderColor="divider"
            borderRadius={1}
            width="100%"
          >
            <SummaryValue label="Dossiers" value={overview.totals.dossierCount} />
            <SummaryValue label="Finalized" value={overview.totals.finalizedCount} />
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
              <DataGrid
                getRowId={(row) => row.preFinanceId}
                rows={overview.items || []}
                columns={columns}
                disableSelectionOnClick
                density="compact"
                loading={isLoading}
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                sortingOrder={["desc", "asc"]}
                rowCount={overview.totalCount || 0}
                page={pagination.page}
                onPageChange={handlePageChange}
                pageSize={pagination.pageSize}
                onPageSizeChange={handlePageSizeChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                components={{ Toolbar: PrefinanceOverviewToolbar }}
                componentsProps={{
                  toolbar: {
                    columns,
                    fetchRows: handleFetchExportRows,
                    fileName: isGlobalScope ? "all-pre-financing" : "prefinance-dossiers",
                    onError: handleExportError,
                  },
                }}
                sx={{
                  height: "100%",
                  minHeight: 240,
                  borderRadius: 1,
                  boxShadow: 2,
                  "& .MuiDataGrid-cell:hover": {
                    color: "primary.main",
                  },
                }}
              />
            </Box>
        </CardContent>
      </Card>

      <PreFinanceDossierDetailDialog
        dossier={selectedDossier}
        open={Boolean(selectedDossier)}
        onClose={handleCloseDetails}
        customerId={selectedCustomerId}
        customerNumber={selectedCustomerNumber}
        canFinalizePreFinance={canFinalizePreFinance}
        canUnfinalizePreFinance={canUnfinalizePreFinance}
        onRequestDelete={handleRequestDelete}
        onFinalized={handleDossierFinalized}
        onUnfinalized={handleDossierUnfinalized}
        onPayoutAmountAdjusted={handlePayoutAmountAdjusted}
        isDeleting={Boolean(selectedDossier && deletingDossierId === selectedDossier.preFinanceId)}
      />
      {!isGlobalScope && (
        <>
          <VatPreFinancePrepareDialog
            open={isVatPrepareOpen}
            customerId={customerId}
            countries={countries}
            onClose={() => setIsVatPrepareOpen(false)}
            onCreated={handleVatCreated}
          />
          <ExcisePreFinancePrepareDialog
            open={isExcisePrepareOpen}
            customerId={customerId}
            onClose={() => setIsExcisePrepareOpen(false)}
            onCreated={handleExciseCreated}
          />
        </>
      )}
      <ConfirmDialog
        confirmDialog={{
          ...confirmDialog,
          onConfirm: handleDeleteDossier,
        }}
        setConfirmDialog={setConfirmDialog}
      />
      {viewClaimDialog.isOpen && (
        <ViewClaimDialog viewClaimDialog={viewClaimDialog} setViewClaimDialog={setViewClaimDialog} />
      )}
      {viewExciseClaimDialog.isOpen && (
        <ViewExciseClaimDialog
          viewExciseClaimDialog={viewExciseClaimDialog}
          setViewExciseClaimDialog={setViewExciseClaimDialog}
        />
      )}
    </React.Fragment>
  );
};

export default CustomerFinancial;
